<script lang="ts">
	import {
		createRender,
		createTable,
		Render,
		Subscribe,
		type Constructor,
		type DataBodyCell,
		type DataBodyRow,
		type DataLabel
	} from 'svelte-headless-table'
	import Tooltip from '../atoms/Tooltip.svelte'
	import Empty from '../atoms/Empty.svelte'
	import InputFieldTableCell from './InputFieldTableCell.svelte'
	import AnalyticEvaluationTableCell from './AnalyticEvaluationTableCell.svelte'
	import FootnoteIconCell from './FootnoteIconCell.svelte'
	import { createEventDispatcher, getContext, setContext, type SvelteComponent } from 'svelte'
	import { AnalyticalType } from '$types/enums'
	import RemoveAnalysisValueCell from './RemoveAnalysisValueCell.svelte'
	import { inputFieldFormats } from '$utils/formatters'
	import ttImg from '@/icons/icon_info_blue_outline.svg'
	import { derived as derivedStore, type Writable, writable } from 'svelte/store'
	import { analyticalMetadata, hideOptionalValues, values } from './stores'
	import type { AnalyticDto } from './types/analyticDto'
	import type { AnalyticalValue } from './types/analyticalValue'
	import type { Result } from './types/analyticalEvaluation'
	import AnalysisErrorTour from './AnalysisErrorTour.svelte'
	import AnalysisScaleBadge from '$lib/analytics/AnalysisScaleBadge.svelte'

	setContext('useMaterialDesign', false)
	const dispatch = createEventDispatcher()
	const stickyHeaderTop: Writable<string> = getContext('stickyHeightInformation')
	const SEARCH_DEBOUNCE_MS = 500

	// For recommendations, we need to pass the analysis values manually, otherwise, the standard store is used.
	export let analysisValues: Writable<AnalyticalValue[]> = values
	export let analytic: AnalyticDto | null = null
	export let searchTerm: string = ''
	export let editable: boolean = true
	export let compact: boolean = false

	let type: AnalyticalType | null = $analyticalMetadata.analyticalType

	// Needs to be a store to react to inputs and filter rows
	const searchTermStore = writable(searchTerm)
	$: searchTermStore.set(searchTerm)
	// Need another store to debounce the search term
	const debouncedSearchTerm = derivedStore(
		searchTermStore,
		($term, set) => {
			// HINT: setTimeout(func, delay, param1) https://developer.mozilla.org/en-US/docs/Web/API/Window/setTimeout
			const handle = setTimeout(set, SEARCH_DEBOUNCE_MS, $term)
			return () => clearTimeout(handle)
		},
		searchTerm
	)

	function filterRow(item: AnalyticalValue, hideOptionalVals: boolean, searchTerm: string) {
		// If a value is optional but filled, we still show it.
		let isOptionalAndNotFilled = analytic
			? analytic.optionalValues.includes(item.key) && !item.value
			: false
		let isMatchingSearch = searchTerm
			? stringMatch(item.name, searchTerm) ||
				stringMatch(item.extractionMethod, searchTerm) ||
				stringMatch(item.title, searchTerm) ||
				stringMatch(item.unit, searchTerm)
			: true // TODO better search matching

		return (
			(!hideOptionalVals || (hideOptionalVals && !isOptionalAndNotFilled && !item.additional)) &&
			isMatchingSearch
		)
	}

	const stringMatch = (text: string | undefined, searchTerm: string) => {
		let matches = false
		if (text) {
			matches = text.toLowerCase().includes(searchTerm.toLowerCase())
		}
		return matches
	}

	const showTitle = (
		filteredRows: DataBodyRow<AnalyticalValue>[],
		idx: number,
		item: AnalyticalValue
	) => {
		if (compact) {
			return false
		}

		if (idx === 0) {
			return true
		}

		return filteredRows[idx - 1].original.title !== item.title
	}

	// noinspection TypeScriptValidateTypes
	const EditableCell: DataLabel<AnalyticalValue> /*: DataLabel<Sample>*/ = ({
		column,
		row,
		value
	}) =>
		createRender(InputFieldTableCell, {
			row,
			column,
			value,
			onUpdateValue: updateData,
			isNumeric: false,
			placeholder: 'Wert',
			editable,
			format: compact ? inputFieldFormats.COMPACT : inputFieldFormats.NORMAL,
			analyticalValue: (row as DataBodyRow<AnalyticalValue>).original
		})

	const updateData = (rowDataId: string, columnId: string, newValue: number) => {
		// In this case, the dataId of each item is its index in $data.
		// You can also handle any server-synchronization necessary here.
		const idx = parseInt(rowDataId)
		const currentItem = $analysisValues[idx]
		const key = columnId as keyof AnalyticalValue
		const newItem = { ...currentItem, [key]: newValue }
		$analysisValues[idx] = newItem
		$analysisValues = $analysisValues
		dispatch('updateValue')
	}

	// noinspection TypeScriptValidateTypes
	const EvaluationCell: DataLabel<AnalyticalValue> = ({ value }) =>
		createRender(
			AnalyticEvaluationTableCell as unknown as Constructor<
				SvelteComponent<{ evaluation: Record<string, Result>; compact: boolean }>
			>,
			{
				evaluation: value as Record<string, Result>,
				compact: compact
			}
		)

	const EvaluationResultCell: DataLabel<AnalyticalValue> = ({ value }) => {
		if (!value) {
			// noinspection TypeScriptValidateTypes
			return createRender(Empty, {})
		}
		const evaluation: [string, Result][] = Object.entries(value)
		if (
			evaluation.length === 1 &&
			evaluation[0][1].value !== undefined &&
			evaluation[0][1].description !== '/'
		) {
			const firstValue = evaluation[0][1]
			return createRender(
				AnalysisScaleBadge as unknown as Constructor<
					SvelteComponent<{
						ordinal: number
						maxOrdinal: number
						description: string
						style: string
					}>
				>,
				{
					ordinal: firstValue.priority,
					maxOrdinal: firstValue.maxOrdinal,
					description: firstValue.description,
					style: 'width: fit-content'
				}
			)
		}
		// noinspection TypeScriptValidateTypes
		return createRender(Empty, {})
	}

	const valuesTable = createTable(analysisValues)
	let cols = []

	cols.push(
		valuesTable.column({
			header: 'Stoff',
			accessor: 'name'
		})
	)
	cols.push(
		valuesTable.column({
			header: '',
			accessor: 'title',
			cell: ({ row }: DataBodyCell<AnalyticalValue>) => {
				const dataRow = row as DataBodyRow<AnalyticalValue>
				if (dataRow.original.title || dataRow.original.extractionMethod) {
					// noinspection TypeScriptValidateTypes
					return createRender(Tooltip, {
						msg: [dataRow.original.title, dataRow.original.extractionMethod].join(' '),
						ttipIconImage: ttImg
					})
				} else {
					// noinspection TypeScriptValidateTypes
					return createRender(Empty, {})
				}
			}
		})
	)
	cols.push(
		valuesTable.column({
			header: 'Einheit',
			accessor: (item) => (item.unit ? item.unit : '')
		})
	)
	cols.push(
		valuesTable.column({
			header: type === AnalyticalType.ANALYTIC ? 'Grenzwert' : 'Wert',
			accessor: 'value',
			cell: EditableCell
		})
	)
	if (!compact && type === AnalyticalType.ANALYSIS) {
		cols.push(
			valuesTable.column({
				header: 'Ergebnis',
				accessor: (item) => item?.evaluation,
				cell: EvaluationResultCell
			})
		)
	}
	if (type === AnalyticalType.ANALYSIS) {
		cols.push(
			valuesTable.column({
				header: 'Auswertung',
				accessor: 'evaluation',
				cell: EvaluationCell
			})
		)
	}
	if (!compact) {
		cols.push(
			valuesTable.column({
				header: '',
				accessor: 'footnote',
				cell: ({ row }: DataBodyCell<AnalyticalValue>) => {
					const dataRow = row as DataBodyRow<AnalyticalValue>
					if (dataRow.original.additional) {
						// noinspection TypeScriptValidateTypes
						return createRender(RemoveAnalysisValueCell, {
							valueKey: dataRow.original.key,
							editable,
							onValuesRemoved: () => dispatch('valuesRemoved')
						})
					} else {
						// noinspection TypeScriptValidateTypes
						return createRender(FootnoteIconCell, {
							footnote: dataRow.original.footnote
						})
					}
				}
			})
		)
	}

	let valuesColumns = valuesTable.createColumns(cols)

	const { headerRows, rows, tableAttrs, tableBodyAttrs } =
		valuesTable.createViewModel(valuesColumns)

	const errorValues = writable<AnalyticalValue[]>([])
	const filteredRows = derivedStore(
		[rows, hideOptionalValues, debouncedSearchTerm],
		([rowData, hideOptionalVals, debouncedSearch]) => {
			const dataRows = rowData as DataBodyRow<AnalyticalValue>[]
			let rowsTemp = dataRows.filter((r) =>
				filterRow(r.original, hideOptionalVals, debouncedSearch)
			)
			// Needed so we do not trigger a new tour just by changing the readingError field of a value
			if ($filteredRows?.length !== rowsTemp.length) {
				$errorValues = rowsTemp.map((r) => r.original).filter((v) => v.readingError)
			}
			return rowsTemp
		},
		[] as DataBodyRow<AnalyticalValue>[]
	)

	function onValueFixed(fixedVal: AnalyticalValue) {
		let idx = $analysisValues.findIndex((v) => v.key === fixedVal.key)
		if (idx !== -1) {
			$analysisValues[idx] = { ...$analysisValues[idx], readingError: false }
		}
	}

	// FIXME: dead reactive — columns array is built once at script init using initial `type`,
	// so reassigning `type` later never propagates to rendered columns/headers.
	// Either remove this line, or rebuild columns reactively when `type` changes.
	// eslint-disable-next-line @typescript-eslint/no-unused-expressions, no-useless-assignment -- see FIXME above
	$: ($analyticalMetadata.analyticalType, (type = $analyticalMetadata.analyticalType))
</script>

{#if !compact}
	<AnalysisErrorTour {errorValues} {onValueFixed} />
{/if}

<table {...$tableAttrs} class="analysis-table" class:compact>
	<thead>
		{#each $headerRows as headerRow (headerRow.id)}
			<Subscribe rowAttrs={headerRow.attrs()} let:rowAttrs>
				<tr
					{...rowAttrs}
					class="analysis-table-header"
					style="--sticky-header-top: {$stickyHeaderTop}"
				>
					{#each headerRow.cells as cell (cell.id)}
						<Subscribe attrs={cell.attrs()} let:attrs>
							<th
								{...attrs}
								class="analysis-table-header-cell analysis-table-header-cell-{cell.id}"
							>
								<Render of={cell.render()} />
							</th>
						</Subscribe>
					{/each}
				</tr>
			</Subscribe>
		{/each}
	</thead>
	<tbody {...$tableBodyAttrs}>
		{#each $filteredRows as row, i (row.id)}
			<Subscribe rowAttrs={row.attrs()} let:rowAttrs>
				{#if showTitle($filteredRows, i, row.original)}
					<tr class="analysis-table-section">
						<td colspan="7">{row.original.title ? row.original.title : 'ohne Kategorie'}</td>
					</tr>
				{/if}
				<tr {...rowAttrs} class="analysis-table-row" id={row.original.key}>
					{#each row.cells as cell (cell.id)}
						<Subscribe attrs={cell.attrs()} let:attrs>
							<td {...attrs} class="analysis-table-cell analysis-table-cell-{cell.id}">
								<Render of={cell.render()} />
							</td>
						</Subscribe>
					{/each}
				</tr>
			</Subscribe>
		{/each}
	</tbody>
</table>

<style lang="scss">
	@import '$styles/global';

	.analysis-table {
		width: 100%;
	}

	.analysis-table-header {
		@include roboto-font(1.125rem, 400, 1rem);

		height: 24px;
		box-shadow: var(
			--table-shadow-bottom,
			inset 0 -1px 0 $primaryGreen
		); // we use this with position sticky to have a border at the bottom

		position: sticky;
		z-index: 1;
		background-color: white;

		// Two times padding of 1.88rem (~30px) + 61px header
		top: calc(var(--sticky-header-top, 30px) + var(--sticky-header-top-padding, 121px));
	}

	.analysis-table-header-cell {
		padding: 10px 13px;
	}

	.compact .analysis-table-header-cell {
		@include roboto-font(18px, 400, 10px, #000000);

		padding: 10px 13px;
	}

	.analysis-table-header-cell-name {
		padding-left: 24px;
	}

	.analysis-table-section {
		background: linear-gradient(
			90deg,
			rgba(192, 192, 192, 0.2) 99.99%,
			rgba(217, 217, 217, 0) 100%
		);
		border-bottom: 1px solid #e9e9e9;
		height: 33px;
	}

	.analysis-table-section > td {
		padding-left: 24px;
	}

	.analysis-table-row {
		border-bottom: 1px solid #e9e9e9;
	}

	.compact.analysis-table > tbody > tr:last-child {
		border-bottom: none;
	}

	.analysis-table-cell {
		padding: 13px;
	}

	.compact .analysis-table-cell {
		@include roboto-font(18px, 400, 10px, #000000);
	}

	.analysis-table-cell-name {
		padding-left: 24px;
	}

	.compact .analysis-table-cell-name {
		padding-left: 13px;
	}

	.analysis-table-cell-title {
		padding-right: 30px;
	}
</style>
